/**
 * The category chips and budget bands themselves — nothing else.
 *
 * Shared by the two places this one setting is edited: the first-run modal
 * over the feed (components/discovery/PreferenceSetup) and the profile's own
 * page (pages/FeedPreferences). They differ in framing, in when they save, and
 * in what surrounds them; the controls are identical, and two copies of a
 * chip grid is how they quietly stop being identical.
 *
 * Deliberately presentational: it owns no state, fetches nothing, and saves
 * nothing. Both callers already have to talk to
 * `/profile/discovery-preferences` for their own reasons — the modal to decide
 * whether it has been answered, the page to load it — and giving this
 * component its own opinion about persistence would mean two components
 * racing to write the same row.
 *
 * `options` is the server's own payload (see
 * routes/mira/profile.py::get_discovery_preferences), not a local constant, so
 * the shopper-facing groups and the taxonomy they resolve to cannot drift.
 */
import React from 'react';
import { Check } from 'lucide-react';

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 font-mahi-sans mb-2">
    {children}
  </p>
);

export default function PreferencePicker({
  options, picked, band, onToggleCategory, onSelectBand, disabled = false, idPrefix = 'pref',
}) {
  return (
    <>
      <SectionLabel>Categories</SectionLabel>
      <div className="flex flex-wrap gap-2 mb-6">
        {options.categories.map((group) => {
          const on = picked.includes(group.key);
          return (
            <button
              key={group.key}
              type="button"
              disabled={disabled}
              onClick={() => onToggleCategory(group.key)}
              aria-pressed={on}
              data-testid={`${idPrefix}-category-${group.key}`}
              title={group.sublabel}
              className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[13px] font-mahi-sans ring-1 transition-colors disabled:opacity-50 ${
                on
                  ? 'bg-raspberry-500 text-white ring-raspberry-500'
                  : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
              }`}
            >
              {on && <Check className="w-3.5 h-3.5" strokeWidth={2.5} />}
              {group.label}
            </button>
          );
        })}
      </div>

      <SectionLabel>Budget</SectionLabel>
      <div className="flex flex-col gap-1.5">
        {options.price_bands.map((option) => {
          const on = band === option.key;
          return (
            <button
              key={option.key}
              type="button"
              disabled={disabled}
              onClick={() => onSelectBand(option.key)}
              aria-pressed={on}
              data-testid={`${idPrefix}-price-${option.key}`}
              className={`text-left px-4 py-2.5 rounded-2xl ring-1 transition-colors disabled:opacity-50 ${
                on ? 'bg-mahiteal-50 ring-mahiteal-200' : 'bg-white ring-slate-200'
              }`}
            >
              <p className={`text-[14px] font-semibold font-mahi-sans ${on ? 'text-mahiteal-800' : 'text-slate-900'}`}>
                {option.label}
              </p>
              <p className="text-[12px] text-slate-500 font-mahi-sans mt-0.5">{option.sublabel}</p>
            </button>
          );
        })}
      </div>
    </>
  );
}

/**
 * The line under the chips, in both places: what "nothing picked" means, and
 * the promise that a narrow pick will not dead-end. Exported alongside the
 * picker because it is the same reassurance in both, and because getting it
 * wrong in one place would misstate how the filter actually behaves.
 */
export const pickerFootnote = (pickedCount) => (pickedCount
  ? "I'll widen past these rather than run out - and say so when I do."
  : "Nothing picked, so I'll show you everything.");
